import imagesLoaded from 'imagesloaded';
import Masonry from 'masonry-layout';
import Offcanvas from 'smg/offcanvas';
import QueryString from 'smg/querystring';
import { UUID } from 'smg/uuid';
import { FindImage, ImageFromLink, OpenGalleryView } from '../gallery/view';

main();

function main() {
    const $galleryImages = document.querySelector<HTMLElement>('#gallery-images');
    if (!$galleryImages) {
        return;
    }

    const masonry = new Masonry($galleryImages, {
        itemSelector: '.gallery-image-parent:not(.d-none)',
    });

    imagesLoaded($galleryImages).on('progress', () => {
        masonry.layout?.();
    });

    const $filterSections = document.querySelectorAll<HTMLElement>('.filter-section');
    const $filterCheckboxes = document.querySelectorAll<HTMLInputElement>('.filter-checkbox');

    const $imgParents = document.querySelectorAll<HTMLAnchorElement>('.gallery-image-parent');
    const imageDetails = new Map<HTMLElement, Map<string, string>>();
    for (const $imageParent of Array.from($imgParents)) {
        const $img = $imageParent.querySelector('img') as HTMLImageElement;
        const detailsJSON = $img.getAttribute('data-details') || '';
        const details = JSON.parse(detailsJSON) as Record<string, string>;

        const detailsMap = new Map<string, string>();
        for (const [key, value] of Object.entries(details)) {
            detailsMap.set(key, value);
        }

        imageDetails.set($imageParent, detailsMap);
    }

    const compareImageToFilters = (image: HTMLElement, filters: Map<string, Set<string>>) => {
        const details = imageDetails.get(image);
        if (!details) {
            return false;
        }

        let show = true;
        for (const [filterName, filterValue] of filters) {
            if (!details.has(filterName) || !filterValue.has(details.get(filterName) as string)) {
                show = false;
                break;
            }
        }
        return show;
    };

    const getFilteredImages = (filters: Map<string, Set<string>>) => {
        const filteredImages = new Set<HTMLElement>();
        imageDetails.forEach((details, $image) => {
            if (compareImageToFilters($image, filters)) {
                filteredImages.add($image);
            }
        });
        return filteredImages;
    };

    const checkedFilters: () => Map<string, Set<string>> = () => {
        const filters = new Map<string, Set<string>>();
        $filterCheckboxes.forEach($checkbox => {
            if ($checkbox.checked) {
                const filter = String($checkbox.getAttribute('data-filter') || '');
                const value = $checkbox.value;

                if (!filters.get(filter)) {
                    filters.set(filter, new Set([value]));
                } else {
                    filters.get(filter)?.add(value);
                }
            }
        });

        return filters;
    };

    const setFancyboxGallery = () => {
        const $visibleImages = Array.from(document.querySelectorAll('.gallery-image-parent:not(.d-none)')) as HTMLAnchorElement[];
        const uuid = UUID();
        $visibleImages.forEach($image => {
            $image.setAttribute('data-fancybox', uuid);
        });
    };

    const toggleImages = () => {
        const filters = checkedFilters();

        $imgParents.forEach($ => $.classList.remove('d-none'));

        if (filters.size === 0) {
            $filterCheckboxes.forEach($checkbox => {
                $checkbox.disabled = false;
                $checkbox.parentElement?.classList.remove('d-none');
            });

            $filterSections.forEach($section => {
                $section.classList.remove('d-none');
            });

            setFancyboxGallery();

            setTimeout(() => {
                masonry.reloadItems?.();
                masonry.layout?.();
            });
            return;
        }

        const filteredImages = getFilteredImages(filters);
        imageDetails.forEach((details, $image) => {
            if (!filteredImages.has($image)) {
                $image.setAttribute('data-fancybox', 'gallery');
                $image.classList.add('d-none');
            }
        });

        $filterCheckboxes.forEach($checkbox => {
            $checkbox.disabled = false;
            $checkbox.parentElement?.classList.remove('d-none');

            if ($checkbox.checked) {
                return;
            }

            const filter = $checkbox.getAttribute('data-filter') || '';
            const value = $checkbox.value;

            const filtersIfChecked = new Map<string, Set<string>>();
            for (const [filterName, filterValue] of filters) {
                filtersIfChecked.set(filterName, new Set(filterValue));
            }
            if (!filtersIfChecked.has(filter)) {
                filtersIfChecked.set(filter, new Set());
            }
            filtersIfChecked.get(filter)?.add(value);
            const filteredImagesIfChecked = getFilteredImages(filtersIfChecked);

            let filterResultsSame = true;
            if (filteredImagesIfChecked.size !== filteredImages.size) {
                filterResultsSame = false;
            }
            for (const $image of filteredImages) {
                if (!filteredImagesIfChecked.has($image)) {
                    filterResultsSame = false;
                    break;
                }
            }

            if (filteredImagesIfChecked.size === 0 || filterResultsSame) {
                $checkbox.disabled = true;
                $checkbox.parentElement?.classList.add('d-none');
            }
        });

        $filterSections.forEach($section => {
            $section.classList.remove('d-none');
            const $checkboxes = $section.querySelectorAll<HTMLInputElement>('.filter-checkbox');
            if (Array.from($checkboxes).every($checkbox => $checkbox.disabled)) {
                $section.classList.add('d-none');
            }
        });

        setFancyboxGallery();

        setTimeout(() => {
            masonry.reloadItems?.();
            masonry.layout?.();
        });
    };

    let filtersQueryString = QueryString().get('filters');
    if (typeof filtersQueryString === 'undefined') {
        if (window.location.host.includes('challengecoins')) {
            filtersQueryString = 'lineItem=Challenge Coins';
        } else if (window.location.host.includes('pins')) {
            filtersQueryString = 'lineItem=Lapel Pins';
        } else if (window.location.host.includes('patches')) {
            filtersQueryString = 'lineItem=Patches';
        } else if (window.location.host.includes('lanyards')) {
            filtersQueryString = 'lineItem=Lanyards';
        }
    }
    if (filtersQueryString) {
        const filters: Record<string, string[]> = filtersQueryString.split('|').reduce((acc: Record<string, string[]>, filter) => {
            const [key, value] = filter.split('=');
            acc[key] = value.split('_');
            return acc;
        }, {});

        Object.entries(filters).forEach(([filter, values]) => {
            values.forEach(value => {
                const $checkbox = document.querySelector<HTMLInputElement>(`input[data-filter="${CSS.escape(filter)}"][value="${CSS.escape(value)}"]`);
                if ($checkbox) {
                    $checkbox.checked = true;
                }
            });
        });
    }

    const setQueryString = () => {
        const filters = checkedFilters();
        // values joined by `_`, filters joined by `|`
        QueryString().set('filters', Array.from(filters).map(([filter, values]) => `${filter}=${Array.from(values).join('_')}`).join('|'));
    };

    $filterCheckboxes.forEach($checkbox => {
        $checkbox.addEventListener('change', () => {
            setQueryString();
            toggleImages();
        });
    });

    const $filtersParent = document.querySelector('#gallery-filters-parent') as HTMLElement;
    const $filters = document.querySelector('#gallery-filters') as HTMLElement;

    const $showFilters = document.querySelector<HTMLButtonElement>('#gallery-show-filters');
    $showFilters?.addEventListener('click', () => {
        new Offcanvas({
            title: 'Filters',
            body: $filters,
            cancelText: 'Close',
            onClose() {
                $filtersParent.appendChild($filters);
            },
        });
    });

    toggleImages();

    // Every image opens the full-size view, grouped with the images the filters show.
    const visibleImages = () => Array.from(document.querySelectorAll<HTMLAnchorElement>('.gallery-image-parent:not(.d-none)')).map(ImageFromLink);
    $imgParents.forEach($link => {
        $link.addEventListener('click', e => {
            e.preventDefault();
            const images = visibleImages();
            OpenGalleryView(images, images.findIndex(image => image.$link === $link), 'gallery');
        });
    });

    // A link to one image opens it first in the group, even when the filters exclude it.
    const img = QueryString().get('img');
    if (img) {
        const all = Array.from($imgParents).map(ImageFromLink);
        const found = FindImage(all, img);
        if (found === -1) {
            QueryString().delete('img');
            return;
        }
        const images = visibleImages();
        let index = images.findIndex(image => image.$link === all[found].$link);
        if (index === -1) {
            images.unshift(all[found]);
            index = 0;
        }
        OpenGalleryView(images, index, 'gallery');
    }
}
